import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageTitle from "../components/PageTitle";
import GoTo from "../components/GoTo";
import AuthModal from "../components/AuthModal";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

function AccountLoginPage() {
  const { login, signup, resetPassword } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();

  // Helper function to add language prefix to paths
  const getLocalizedPath = (path) => {
    // Split path and query string
    const [pathPart, queryString] = path.split("?");

    if (language === "ar") {
      // Remove /ar if already present to avoid duplication
      const cleanPath = pathPart.startsWith("/ar")
        ? pathPart.replace("/ar", "")
        : pathPart;
      const localizedPath = `/ar${cleanPath === "/" ? "" : cleanPath}`;
      return queryString ? `${localizedPath}?${queryString}` : localizedPath;
    }
    // Remove /ar prefix if present when switching to English
    const englishPath = pathPart.startsWith("/ar")
      ? pathPart.replace("/ar", "") || "/"
      : pathPart;
    return queryString ? `${englishPath}?${queryString}` : englishPath;
  };

  const [form, setForm] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [currentView, setCurrentView] = useState("login");
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState("forgot");

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(form.email, form.password);
      navigate(getLocalizedPath("/account"));
    } catch (err) {
      setError(err?.message || (language === "ar" ? "فشل تسجيل الدخول" : "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    // Validate all required fields
    if (!form.firstName.trim()) {
      setError(language === "ar" ? "الاسم الأول مطلوب" : "First name is required");
      setSubmitting(false);
      return;
    }
    if (!form.lastName.trim()) {
      setError(language === "ar" ? "اسم العائلة مطلوب" : "Last name is required");
      setSubmitting(false);
      return;
    }
    if (!form.email.trim()) {
      setError(language === "ar" ? "البريد الإلكتروني مطلوب" : "Email is required");
      setSubmitting(false);
      return;
    }
    if (!form.password.trim()) {
      setError(language === "ar" ? "كلمة المرور مطلوبة" : "Password is required");
      setSubmitting(false);
      return;
    }
    if (!form.phone.trim()) {
      setError(language === "ar" ? "رقم الهاتف مطلوب" : "Phone is required");
      setSubmitting(false);
      return;
    }

    try {
      const name = [form.firstName, form.lastName].filter(Boolean).join(" ");
      await signup(form.email, form.password, name, form.phone);
      // After signup, auto-login
      await login(form.email, form.password);
      navigate(getLocalizedPath("/account"));
    } catch (err) {
      setError(err?.message || (language === "ar" ? "فشل التسجيل" : "Signup failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <style>
        {`
          /* Enhanced Login Page Styling */
          .tf-login-wrap {
            gap: 40px;
          }

          .tf-login-form {
            background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
            border-radius: 20px;
            padding: 50px 60px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
            width: 100%;
            max-width: 100%;
            border: 1px solid rgba(14, 90, 167, 0.1);
            position: relative;
            overflow: hidden;
          }

          .tf-login-form::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(90deg, #0e5aa7 0%, #0a4a8a 100%);
          }

          .tf-login-content {
            background: #ffffff;
            border-radius: 16px;
            padding: 40px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          }

          .tf-login-form h5,
          .tf-login-content h5 {
            font-size: 32px;
            font-weight: 700;
            color: #1a1a1a;
            margin-bottom: 40px;
            letter-spacing: -0.5px;
            position: relative;
          }

          .tf-login-form h5::after {
            content: '';
            position: absolute;
            bottom: -12px;
            left: 0;
            width: 60px;
            height: 3px;
            background: linear-gradient(90deg, #0e5aa7 0%, #0a4a8a 100%);
            border-radius: 2px;
          }

          .tf-field {
            position: relative;
            margin-bottom: 20px;
          }

          .tf-field-input {
            width: 100%;
            padding: 16px 20px;
            border: 2px solid #e8e8e8;
            border-radius: 12px;
            font-size: 15px;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            background: #ffffff;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          }

          .tf-field-input:hover {
            border-color: #0e5aa7;
            box-shadow: 0 4px 12px rgba(14, 90, 167, 0.1);
          }

          .tf-field-input:focus {
            outline: none;
            border-color: #0e5aa7;
            background: #ffffff;
            box-shadow: 0 0 0 4px rgba(14, 90, 167, 0.15), 0 4px 16px rgba(14, 90, 167, 0.2);
            transform: translateY(-1px);
          }

          .tf-field-label {
            position: absolute;
            left: 16px;
            top: 14px;
            font-size: 15px;
            color: #666;
            pointer-events: none;
            transition: all 0.3s ease;
          }

          .tf-field-input:focus + .tf-field-label,
          .tf-field-input:not(:placeholder-shown) + .tf-field-label {
            top: -10px;
            left: 12px;
            font-size: 12px;
            color: #0e5aa7;
            background: #ffffff;
            padding: 0 4px;
          }

          .tf-btn {
            padding: 14px 24px;
            font-weight: 600;
            font-size: 16px;
            border-radius: 10px;
            transition: all 0.3s ease;
          }

          .tf-btn.btn-fill {
            background: linear-gradient(135deg, #0e5aa7 0%, #0a4a8a 100%);
            color: #ffffff;
            border: none;
            box-shadow: 0 6px 20px rgba(14, 90, 167, 0.3);
            position: relative;
            overflow: hidden;
          }

          .tf-btn.btn-fill::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
            transition: left 0.5s;
          }

          .tf-btn.btn-fill:hover:not(:disabled)::before {
            left: 100%;
          }

          .tf-btn.btn-fill:hover:not(:disabled) {
            transform: translateY(-3px);
            box-shadow: 0 8px 24px rgba(14, 90, 167, 0.4);
          }

          .tf-btn.btn-fill:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .tf-btn.btn-line {
            color: inherit;
            border: none;
            background: transparent;
            text-decoration: none;
            padding: 0;
            box-shadow: none;
          }

          .tf-btn.btn-line:hover {
            background: transparent;
            color: inherit;
            text-decoration: underline;
            transform: none;
            box-shadow: none;
          }

          .text-danger {
            color: #d32f2f;
            font-size: 14px;
            padding: 10px;
            background: #fee;
            border-radius: 8px;
            border: 1px solid #fcc;
          }

          /* Make login form full width */
          .tf-login-wrap {
            display: block;
            padding: 0 40px;
          }

          .tf-login-form {
            width: 100%;
            max-width: 100%;
          }

          /* Mobile responsive */
          @media (max-width: 767px) {
            .tf-login-wrap {
              padding: 0 20px;
            }

            .tf-login-form {
              padding: 32px 24px;
              border-radius: 16px;
              max-width: 100%;
            }

            .tf-login-form h5 {
              font-size: 24px;
              margin-bottom: 24px;
            }

            .tf-field {
              margin-bottom: 16px;
            }

            .tf-field-input {
              padding: 12px 14px;
              font-size: 14px;
            }

            .tf-btn {
              padding: 12px 20px;
              font-size: 15px;
            }

            .tf-login-content {
              display: none;
            }
          }

          @media (min-width: 768px) {
            .tf-login-content {
              display: none;
            }
            
            /* Show register button in login form on desktop too since content section is hidden */
            .register-inline {
              display: inline-block !important;
            }
          }
        `}
      </style>
      <Header />
      <PageTitle title={language === "ar" ? "تسجيل الدخول" : "Login"} />
      <section className="flat-spacing-10">
        <div className="container">
          <div className="tf-grid-layout lg-col-2 tf-login-wrap">
            <div className="tf-login-form">
              {currentView === "login" && (
                <div id="login">
                  <h5 className="mb_36">{language === "ar" ? "تسجيل الدخول" : "Log in"}</h5>
                  <div>
                    <form onSubmit={handleLogin}>
                      <div className="tf-field style-1 mb_15">
                        <input
                          className="tf-field-input tf-input"
                          placeholder=""
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={onChange}
                          required
                        />
                        <label className="tf-field-label fw-4 text_black-2">
                          {language === "ar" ? "البريد الإلكتروني *" : "Email *"}
                        </label>
                      </div>
                      <div className="tf-field style-1 mb_30">
                        <input
                          className="tf-field-input tf-input"
                          placeholder=""
                          type="password"
                          name="password"
                          value={form.password}
                          onChange={onChange}
                          required
                        />
                        <label className="tf-field-label fw-4 text_black-2">
                          {language === "ar" ? "كلمة المرور *" : "Password *"}
                        </label>
                      </div>
                      {error && <div className="text-danger mb_2">{error}</div>}
                      <div className="mb_20 d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <a
                          href="#recover"
                          className="tf-btn btn-line"
                          onClick={(e) => {
                            e.preventDefault();
                            setAuthModalView("forgot");
                            setIsAuthModalOpen(true);
                          }}
                        >
                          {language === "ar" ? "نسيت كلمة المرور؟" : "Forgot your password?"}
                        </a>
                        <a
                          href="#register"
                          className="tf-btn btn-line register-inline"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentView("register");
                          }}
                        >
                          {language === "ar" ? "التسجيل" : "Register"}
                        </a>
                      </div>
                      <div>
                        <button
                          type="submit"
                          className="tf-btn w-100 radius-3 btn-fill animate-hover-btn justify-content-center"
                          disabled={submitting}
                        >
                          {submitting
                            ? (language === "ar" ? "جاري تسجيل الدخول..." : "Logging in...")
                            : (language === "ar" ? "تسجيل الدخول" : "Log in")}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {currentView === "register" && (
                <div id="register">
                  <h5 className="mb_36">{language === "ar" ? "التسجيل" : "Register"}</h5>
                  <div>
                    <form onSubmit={handleSignup}>
                      <div className="tf-field style-1 mb_15">
                        <input
                          className="tf-field-input tf-input"
                          placeholder=""
                          type="text"
                          name="firstName"
                          value={form.firstName}
                          onChange={onChange}
                          required
                        />
                        <label className="tf-field-label fw-4 text_black-2">
                          {language === "ar" ? "الاسم الأول *" : "First name *"}
                        </label>
                      </div>
                      <div className="tf-field style-1 mb_15">
                        <input
                          className="tf-field-input tf-input"
                          placeholder=""
                          type="text"
                          name="lastName"
                          value={form.lastName}
                          onChange={onChange}
                          required
                        />
                        <label className="tf-field-label fw-4 text_black-2">
                          {language === "ar" ? "اسم العائلة *" : "Last name *"}
                        </label>
                      </div>
                      <div className="tf-field style-1 mb_15">
                        <input
                          className="tf-field-input tf-input"
                          placeholder=""
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={onChange}
                          required
                        />
                        <label className="tf-field-label fw-4 text_black-2">
                          {language === "ar" ? "البريد الإلكتروني *" : "Email *"}
                        </label>
                      </div>
                      <div className="tf-field style-1 mb_15">
                        <input
                          className="tf-field-input tf-input"
                          placeholder=""
                          type="password"
                          name="password"
                          value={form.password}
                          onChange={onChange}
                          required
                        />
                        <label className="tf-field-label fw-4 text_black-2">
                          {language === "ar" ? "كلمة المرور *" : "Password *"}
                        </label>
                      </div>
                      <div className="tf-field style-1 mb_30">
                        <input
                          className="tf-field-input tf-input"
                          placeholder=""
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={onChange}
                          required
                        />
                        <label className="tf-field-label fw-4 text_black-2">
                          {language === "ar" ? "رقم الهاتف *" : "Phone *"}
                        </label>
                      </div>
                      {error && <div className="text-danger mb_2">{error}</div>}
                      <div className="mb_20">
                        <a
                          href="#login"
                          className="tf-btn btn-line"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentView("login");
                          }}
                        >
                          {language === "ar" ? "لديك حساب بالفعل؟ تسجيل الدخول" : "Already have an account? Log in"}
                        </a>
                      </div>
                      <div>
                        <button
                          type="submit"
                          className="tf-btn w-100 radius-3 btn-fill animate-hover-btn justify-content-center"
                          disabled={submitting}
                        >
                          {submitting
                            ? (language === "ar" ? "جاري الإنشاء..." : "Creating...")
                            : (language === "ar" ? "التسجيل" : "Register")}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
            <div className="tf-login-content">
              <h5 className="mb_36">{language === "ar" ? "أنا جديد هنا" : "I'm new here"}</h5>
              <a
                href="#register"
                className="tf-btn btn-line"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentView("register");
                }}
              >
                {language === "ar" ? "التسجيل" : "Register"}<i className="icon icon-arrow1-top-left"></i>
              </a>
            </div>
          </div>
        </div>
      </section>
      <Footer />
      <GoTo />
      <AuthModal
        isOpen={isAuthModalOpen}
        view={authModalView}
        onClose={() => setIsAuthModalOpen(false)}
        onChangeView={(v) => setAuthModalView(v)}
      />
    </div>
  );
}

export default AccountLoginPage;
